import * as React from "react";
import { Button, Card, notification } from "antd";
import { EditOutlined, DownloadOutlined, PlusCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { envInputToText } from "../../workload/draft/utils/envVar";
import { FormListFieldLabels } from "../formListFieldLabels";
import { FormListFieldItem } from "../formListFieldItem";
import { EnvVarsEditor } from "./envVarsEditor";
import { EnvVarItem } from "./envVarItem";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { formSet, formSetValue, useCpFormAnyValue, useCpFormValue } from "../testNewForm/utils";
import { cloneDeep } from "lodash";
import { store } from "../../../store";
import { EnvVarInput } from "../../../schema/types/workload/input";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { GvcInput } from "../../../schema/types/gvc/input";
import { CpErrorWarning } from "../testNewForm/CpErrorWarning";
import { getDefaultKindSelectState } from "../../../components/antd/kindselect/KindSelect";
import { ImportButton, envVarTransformer } from "../../../components/import";
import { useRevealEnvSecrets } from "../../../reactHooks/useRevealEnvSecrets";

interface Props {}
export const Editable: React.FC<Props> = ({}) => {
  const context = useAppSelector(selectContext);
  const { submit, id } = useCpFormContext();

  const [isEditorOpen, setIsEditorOpen] = React.useState(false);

  const name = useCpFormValue(`name`);
  const submitting = useCpFormAnyValue(`meta.submitting`);
  const env: EnvVarInput[] = useCpFormValue(`env`);
  const { revealState, handleRevealAll, handleHideAll } = useRevealEnvSecrets(env);
  const hasSecretEnvVars = React.useMemo(
    () => env.some((e) => e.prefix === "cpln://secret/" && e.cplnSecret.value),
    [env],
  );

  async function handleExport(): Promise<void> {
    try {
      const form = store.getState().form.instances[id];
      const values: GvcInput = form.values as any;

      const envVars = cloneDeep(values.env);
      const blob = envInputToText(envVars);
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      let filename = "";
      if (context.org) {
        filename += `${context.org}`;
      }
      if (name) {
        filename += `-${name}`;
      }
      a.download = `${filename}.env`;
      const file = new Blob([blob], { type: "text/plain" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: "Try again" });
    }
  }

  function add() {
    const _items = cloneDeep(env);
    _items.push({
      id: uuidv4(),
      name: "",
      prefix: "",
      text: "",
      cplnSecret: getDefaultKindSelectState(),
      k8sSecret: "",
      reference: "",
      suffix: "",
    });
    formSetValue(id, `env`, _items);

    const form = store.getState().form.instances[id];
    const onBlurCounter = form.meta.onBlurCounter;
    formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
  }

  // <NGAlert
  //   type="info"
  //   className="mt-4"
  //   message="Any change will initiate the deployment of workloads that have the 'inheritEnv' flag enabled in any of their containers"
  // />

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-baseline`}>
              <div>Environment Variables</div>
              <div className="flex-grow" />
              <div className="flex items-center gap-4">
                {hasSecretEnvVars ? (
                  <Button
                    htmlType="button"
                    onClick={revealState.isRevealed ? handleHideAll : handleRevealAll}
                    size="small"
                    type="default"
                    loading={revealState.isRevealing}
                    icon={revealState.isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    iconPosition="end"
                  >
                    {revealState.isRevealed ? "Hide Secrets" : "Reveal Secrets"}
                  </Button>
                ) : null}
                <Button
                  htmlType={`button`}
                  onClick={() => setIsEditorOpen(true)}
                  size={"small"}
                  type={"default"}
                  icon={<EditOutlined />}
                  iconPosition="end"
                >
                  Editor
                </Button>
                <ImportButton<EnvVarInput[], EnvVarInput[]>
                  formId={id}
                  fieldName="env"
                  actionLabel="Import environment variables"
                  existingValue={env}
                  transform={(parsed, existing) => envVarTransformer(parsed, existing)}
                  explanation={
                    <div className="text-xs">
                      <div className="font-semibold mb-1">Supported formats:</div>
                      <div><strong>.env</strong> — KEY=value</div>
                      <div><strong>.yaml</strong> — key: value</div>
                      <div><strong>.json</strong> — {`{"key": "value"}`}</div>
                      <div className="mt-2 font-semibold">Special prefixes:</div>
                      <div><strong>cpln://secret/</strong> — Control Plane secret</div>
                      <div><strong>k8s://secret/</strong> — Kubernetes secret</div>
                      <div><strong>cpln://reference/</strong> — Reference value</div>
                      <div className="mt-1 opacity-75">Duplicate keys will be overwritten</div>
                    </div>
                  }
                />
                <Button
                  htmlType={`button`}
                  onClick={() => handleExport()}
                  size={"small"}
                  type={"default"}
                  icon={<DownloadOutlined />}
                  iconPosition="end"
                >
                  Export
                </Button>
              </div>
            </div>
          </CardTitle>
          <CardContent>
            <div>
              {env.length < 1 ? null : (
                <FormListFieldLabels
                  className={`mb-1`}
                  fieldLabels={[
                    { label: "Name", span: 3 },
                    { label: "Value", span: 1, minWidth: 160 },
                    { label: "", span: 1 },
                  ]}
                  columnCount={8}
                  hasRemoveButton
                />
              )}
              {env.map((env, envVarIndex) => {
                return (
                  <FormListFieldItem columnCount={8} key={env.id} hasRemoveButton className="mb-1">
                    <EnvVarItem index={envVarIndex} revealState={revealState} />
                  </FormListFieldItem>
                );
              })}
              <Button type="dashed" icon={<PlusCircleOutlined />} onClick={() => add()}>
                Add Environment Variable
              </Button>
              <CpErrorWarning name={`envroot`} />
            </div>
          </CardContent>
        </Card>
      </div>
      <EnvVarsEditor isOpen={isEditorOpen} onClose={() => setIsEditorOpen(false)} />
    </>
  );
};

// when inputs the same name
{
  /* <NGAlert
  type="error"
  className="mt-2 break-all"
  message={`The variable name "${envVar.name}" is already in use.`}
/> */
}

// when secret value contains space
{
  /* <NGAlert
  type="error"
  className="mt-2"
  message={`Empty variable values are not allowed when referencing secrets.`}
/> */
}

// when has the same name as gvc env var name
{
  /* <NGAlert
  type="warning"
  renderIcon={() => null as any}
  render={() => {
    return (
      <div className="text-sm">
        <div>Overrides Inherited Env Variable</div>
      </div>
    );
  }}
  size={"small"}
/> */
}
